import React, { useCallback, useEffect, useState } from "react";
import "./style.css";

const TreeNodeDraft = ({
  node,
  depth,
  onToggle,
  setOpenDeleteModal,
  setDeleteNodeId,
  onAdd,
  setOpenEditModal,
  setEditNodeId,
  setEditText,
  onMove,
  setOpenMessageModal,
  setMessageNodeId,
  isRootNode = false
}) => {
  const lastChildDropEl = React.useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropPosition, setDropPosition] = useState(null); // 'before'|'inside'|'after'

  // Helper to check if node has at least one valid message
  const hasValidMessages = () => {
    if (!node.messages) return false;
    const stripHtml = (html) => {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html || "";
      return (tmp.textContent || tmp.innerText || "").trim();
    };
    // Valid if at least one message is present (red only if both are absent)
    return stripHtml(node.messages.userMessage) || stripHtml(node.messages.amiMessage);
  };

  const handleDragStart = useCallback((e) => {
    // store the dragged node id
    e.stopPropagation();
    try {
      e.dataTransfer.setData("text/plain", String(node.id));
      e.dataTransfer.effectAllowed = "move";
    } catch (err) {
      // some browsers may throw when dataTransfer not available
    }
  }, [node.id]);

  const handleDragOver = useCallback((e) => {
    // allow drop
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = "move";
    } catch (err) {}
    setIsDragOver(true);

    // compute drop position relative to the header (before/inside/after)
    try {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const ratio = rect.height > 0 ? offsetY / rect.height : 0.5;
      if (ratio < 0.25) setDropPosition('before');
      else if (ratio > 0.75) setDropPosition('after');
      else setDropPosition('inside');
    } catch (err) {
      setDropPosition(null);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.stopPropagation();
    setIsDragOver(false);
    setDropPosition(null);
  }, []);

  // Helpers for dropping between children
  const clearLastChildDrop = useCallback(() => {
    try {
      if (lastChildDropEl.current) {
        lastChildDropEl.current.classList.remove('drop-before', 'drop-after');
        lastChildDropEl.current = null;
      }
    } catch (err) {}
  }, []);

  const handleChildrenDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    try { e.dataTransfer.dropEffect = 'move'; } catch (err) {}

    // find direct child .tree-node elements
    const container = e.currentTarget;
    const children = Array.from(container.children).filter(c => c.classList && c.classList.contains('tree-node'));
    if (children.length === 0) {
      // empty children area -> indicate inside
      setIsDragOver(true);
      setDropPosition('inside');
      clearLastChildDrop();
      return;
    }

    const clientY = e.clientY;
    let found = false;
    for (const childEl of children) {
      const rect = childEl.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      // clear any previous
      // if cursor is above midpoint -> before this child
      if (clientY < mid) {
        clearLastChildDrop();
        childEl.classList.add('drop-before');
        lastChildDropEl.current = childEl;
        found = true;
        break;
      }
    }
    if (!found) {
      // after last child
      clearLastChildDrop();
      const last = children[children.length - 1];
      last.classList.add('drop-after');
      lastChildDropEl.current = last;
    }
    setIsDragOver(true);
  }, [clearLastChildDrop]);

  const handleChildrenDragLeave = useCallback((e) => {
    e.stopPropagation();
    setIsDragOver(false);
    setDropPosition(null);
    clearLastChildDrop();
  }, [clearLastChildDrop]);

  const handleChildrenDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDropPosition(null);
    let draggedId = null;
    try { draggedId = e.dataTransfer.getData('text/plain'); } catch (err) {}
    if (!draggedId) return;

    // if there are no children, insert inside this node
    const container = e.currentTarget;
    const children = Array.from(container.children).filter(c => c.classList && c.classList.contains('tree-node'));
    if (children.length === 0) {
      if (onMove) onMove(String(draggedId), String(node.id), 'inside');
      clearLastChildDrop();
      return;
    }

    // find which child has drop-before/after class
    let targetChild = null;
    let pos = 'after';
    for (const childEl of children) {
      if (childEl.classList.contains('drop-before')) {
        targetChild = childEl.getAttribute('data-node-id');
        pos = 'before';
        break;
      }
      if (childEl.classList.contains('drop-after')) {
        targetChild = childEl.getAttribute('data-node-id');
        pos = 'after';
        break;
      }
    }

    clearLastChildDrop();

    if (!targetChild) {
      // fallback: append inside
      if (onMove) onMove(String(draggedId), String(node.id), 'inside');
    } else {
      if (onMove) onMove(String(draggedId), String(targetChild), pos);
    }
  }, [clearLastChildDrop, node.id, onMove]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    let draggedId = null;
    try {
      draggedId = e.dataTransfer.getData("text/plain");
    } catch (err) {
      // ignore
    }
    if (!draggedId) return;
    // prevent dropping on self
    if (String(draggedId) === String(node.id)) return;

    // prevent dropping parent into its own descendant
    try {
      const draggedEl = document.querySelector(`[data-node-id="${draggedId}"]`);
      const targetEl = e.currentTarget.closest('.tree-node');
      if (draggedEl && targetEl && draggedEl.contains(targetEl)) {
        return; // invalid drop
      }
    } catch (err) {}

    // determine final position (use dropPosition computed on dragOver, fallback to inside)
    let finalPos = dropPosition;
    if (!finalPos) {
      try {
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetY = e.clientY - rect.top;
        const ratio = rect.height > 0 ? offsetY / rect.height : 0.5;
        if (ratio < 0.25) finalPos = 'before';
        else if (ratio > 0.75) finalPos = 'after';
        else finalPos = 'inside';
      } catch (err) {
        finalPos = 'inside';
      }
    }
    if (onMove) {
      onMove(String(draggedId), String(node.id), finalPos);
    }
    setDropPosition(null);
    clearLastChildDrop();
  }, [node.id, onMove]);

  return (
    <div className={`tree-node ${isDragOver ? 'drag-over' : ''}`} data-depth={depth} data-node-id={node.id}>
      <div className="node-header" draggable onDragStart={handleDragStart} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        <button
          className={`expand-button ${
            node.children.length === 0 ? "empty" : ""
          }`}
          onClick={() => node.children.length > 0 && onToggle(node.id)}
        >
          {node.children.length > 0 ? (node.expanded ? "−" : "+") : ""}
        </button>
        <div
          className="node-content"
          dangerouslySetInnerHTML={{ __html: node.htmlContent }}
        />
        <div className="node-actions">
          <button
            className="btn btn--secondary btn--icon"
            title="Edit Content"
            onClick={() => {
              setOpenEditModal(true);
              setEditNodeId(node.id);
              setEditText(node.htmlContent);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="black"
              className="bi bi-pencil-square"
              viewBox="0 0 16 16"
            >
              <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
              <path
                fillRule="evenodd"
                d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
              />
            </svg>
          </button>
          {!isRootNode && (
            <button
              className="btn btn--secondary btn--icon"
              title={hasValidMessages() ? "Set Messages" : "Set Messages (Required)"}
              onClick={() => {
                setOpenMessageModal(true);
                setMessageNodeId(node.id);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill={hasValidMessages() ? "black" : "red"}
                className="bi bi-card-checklist"
                viewBox="0 0 16 16"
              >
                <path d="M14.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5zm-13-1A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2z" />
                <path d="M7 5.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m-1.496-.854a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 1 1 .708-.708l.146.147 1.146-1.147a.5.5 0 0 1 .708 0M7 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m-1.496-.854a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 0 1 .708-.708l.146.147 1.146-1.147a.5.5 0 0 1 .708 0" />
              </svg>
            </button>
          )}
          <button
            className="btn btn--primary btn--icon"
            title="Add Child"
            onClick={() => onAdd(node.id)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="#FFFFFF"
              className="bi bi-plus"
              viewBox="0 0 16 16"
            >
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
            </svg>
          </button>
          <button
            className="btn btn--outline btn--icon"
            title="Delete Node"
            onClick={() => {
              setOpenDeleteModal(true);
              setDeleteNodeId(node.id);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="red"
              className="bi bi-trash"
              viewBox="0 0 16 16"
            >
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
              <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
            </svg>
          </button>
        </div>
      </div>
      {node.children.length > 0 && node.expanded && (
        <div className="node-children" onDragOver={handleChildrenDragOver} onDragLeave={handleChildrenDragLeave} onDrop={handleChildrenDrop}>
          {node.children.map((child) => (
            <TreeNodeDraft
              key={child.id}
              node={child}
              depth={depth + 1}
              onToggle={onToggle}
              setOpenDeleteModal={setOpenDeleteModal}
              setDeleteNodeId={setDeleteNodeId}
              onAdd={onAdd}
              setOpenEditModal={setOpenEditModal}
              setEditNodeId={setEditNodeId}
              setEditText={setEditText}
              onMove={onMove}
              setOpenMessageModal={setOpenMessageModal}
              setMessageNodeId={setMessageNodeId}
              isRootNode={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeNodeDraft;



// WEBPACK FOOTER //
// ./src/mi-app/adminPanel/AMI/TreeNodeDraft.js